import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Search from 'components/shared/Search';
import { useProfile } from 'context/profile';
import useNow from 'hooks/useNow';
import useWorkflow from 'hooks/useWorkflow';
import { duration, parseDate } from 'lib/date';
import Workflow, { WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';

import {
  OUTCOME_DENIED,
  OUTCOME_NOT_SELECTED,
  OUTCOME_SELECTED,
  recordedOutcome,
  SELECTED_FACILITY_ID_VALUE,
  selectedGroupName,
} from './constants';
import DetailSlideout from './DetailSlideout';
import { DENIAL_REASONS_LOOKUP } from './FacilityDecisionPanel';
import { resolveNotSelectedReason } from './notSelectedReason';
import { LIST_STATUS_DISPLAY } from './referralProgress';
import { sentToSummary } from './ReferralSentTo';
import { TONE_STYLES } from './StatusBadge';

type SortDirection = 'asc' | 'desc';

type SortColumn = 'snf' | 'elapsed';

type FacilityStatus = 'accepted' | 'denied' | 'pending' | 'archived' | 'canceled';

// Explicit background/text pair per status, rather than routing through StatusBadge's shared
// Tone system — this slideout's Accepted/Denied colors (green10+greenHover, red10+redHover)
// don't match any existing Tone (success/danger there resolve to green100/red100 text).
const FACILITY_STATUS_DISPLAY: Record<FacilityStatus, { label: string; background: string; color: string }> = {
  accepted: { label: 'Accepted', background: colors.green10, color: colors.greenHover },
  denied: { label: 'Denied', background: colors.red10, color: colors.redHover },
  pending: { label: 'Pending Decision', background: colors.black5, color: colors.black75 },
  archived: { label: 'Archived', background: colors.black5, color: colors.black75 },
  canceled: { label: 'Canceled', background: colors.black5, color: colors.black75 },
};

const facilityDecision = (workflow: Workflow, groupId: string) =>
  workflow.actions.find((a) => a.kind === 'FacilityDecision' && a.groupId === groupId);

// Reads the Facility Decision step's outcome for one SNF. Returns the determination value
// ('accepted'/'denied') when the action is completed, 'pending' otherwise.
//
// The CANCELED guard mirrors overallListStatus's (referralProgress.ts) so a canceled/archived
// referral's row and its slideout agree, instead of every undecided facility reading "Pending
// Decision" as if the referral were still actionable. Deliberately NOT guarding on isCompleted
// the way overallListStatus does: by the time a referral completes, its FacilityDecision actions
// already carry real accepted/denied outcomes, and overriding those to a blanket status would
// hide which facilities actually won or lost.
function facilityDecisionStatus(workflow: Workflow, groupId: string): FacilityStatus {
  if (workflow.status === WorkflowStatus.CANCELED) return workflow.isArchived ? 'archived' : 'canceled';
  const action = facilityDecision(workflow, groupId);
  if (!action?.isCompleted) return 'pending';
  const determination = workflow.valueFor('facility_determination', action)?.value;
  if (determination === 'accepted') return 'accepted';
  if (determination === 'denied') return 'denied';
  return 'pending';
}

// ── The outcome variant (OLIO-3295) ──────────────────────────────────────────
//
// Once a referral has a recorded outcome the question a person opens this for changes: not "who
// has answered?" but "how did each building end up?". So the table reads SNF / Referral Outcome /
// Reason, per building:
//
//   1. the building itself denied → Denied, with the reason its deny modal recorded;
//   2. the referral is Denied → every building denied by definition; Denied, with the reason when
//      its decision is in the payload;
//   3. the building is the one selected → Selected, no reason to give;
//   4. otherwise Not Selected — because a sibling was chosen ("Different <client> SNF Selected")
//      when the referral is Selected, or for the reason the person recorded when it is Not Selected.
//
// Every read is off the DETAIL payload, fetched when the slideout opens: the denial's
// `denial_description` slug is on the building's Facility Decision action (labelled through the
// run's lookups, the same read FacilityDecisionPanel makes), the not-selected reason is a run value,
// and the winner is the `selected_facility_id` value. Until that payload lands the table shows a
// loading row rather than verdicts read off the list row, which carries no action outputs.
//
// Group scoping: a building the viewer does not hold has no decision rows in the payload at all.
// Such a building's own verdict is unknowable here, so a recorded reason is only attributed to a
// building whose decision is visible — never the coordinator's not-selected reason to a building
// that may in fact have refused the patient.
//
// A canceled or archived referral keeps the Status table even when it carries a decision: its row
// pill says Archived / Canceled (overallListStatus), and the slideout says the same.

type Verdict = 'selected' | 'notSelected' | 'denied';

const NO_REASON = '–';

// The label the deny modal showed for this building's reason, falling back to the slug rather than
// to nothing — an entry is never silently blank (mirrors the api's NotSelectedDetail stance).
function denialReason(workflow: Workflow, groupId: string): string {
  const description = facilityDecision(workflow, groupId)?.outputFor('denial_description');
  if (typeof description !== 'string' || !description) return NO_REASON;
  return workflow.lookupLabel(DENIAL_REASONS_LOOKUP, description) ?? description;
}

// "Reason · <typed detail> · Competitor: <name>", in the words the picker showed — the same line the
// activity feed writes under a Not Selected outcome (Workflow::Narrate::NotSelectedDetail). A reason
// can carry either follow-up or both: the typed detail rides unlabelled because it finishes the
// reason's own sentence, the competitor keeps its label because it does not (OLIO-3419).
//
// Resolution is shared with the Referral Outcome card via resolveNotSelectedReason (OLIO-3357); only
// the formatting differs (the card reads "Reason – <reason>: <typed detail> · <competitor>").
function notSelectedReason(workflow: Workflow): string {
  const resolved = resolveNotSelectedReason(workflow);
  if (!resolved) return NO_REASON;
  const reason = resolved.typedDetail ? `${resolved.reasonLabel} · ${resolved.typedDetail}` : resolved.reasonLabel;

  return resolved.detail ? `${reason} · Competitor: ${resolved.detail}` : reason;
}

function outcomeRow(workflow: Workflow, groupId: string, outcome: string, clientName: string) {
  const decisionVisible = facilityDecision(workflow, groupId) !== undefined;
  if (facilityDecisionStatus(workflow, groupId) === 'denied' || outcome === OUTCOME_DENIED) {
    return { verdict: 'denied' as Verdict, reason: denialReason(workflow, groupId) };
  }
  if (workflow.valueFor(SELECTED_FACILITY_ID_VALUE)?.value === groupId) {
    return { verdict: 'selected' as Verdict, reason: NO_REASON };
  }
  if (outcome === OUTCOME_SELECTED) {
    return { verdict: 'notSelected' as Verdict, reason: `Different ${clientName ? `${clientName} ` : ''}SNF Selected` };
  }
  return { verdict: 'notSelected' as Verdict, reason: decisionVisible ? notSelectedReason(workflow) : NO_REASON };
}

// The header pill: which way the referral went, naming the winner when there is one.
function outcomeBadge(workflow: Workflow, outcome: string) {
  if (outcome === OUTCOME_SELECTED) {
    const winner = selectedGroupName(workflow);
    return { verdict: 'selected' as Verdict, label: winner ? `Selected: ${winner}` : 'Selected' };
  }
  if (outcome === OUTCOME_DENIED) return { verdict: 'denied' as Verdict, label: 'Denied' };
  if (outcome === OUTCOME_NOT_SELECTED) return { verdict: 'notSelected' as Verdict, label: 'Not Selected' };
  return null;
}

// The same words and tones the list's Referral Outcome pill uses (LIST_STATUS_DISPLAY), so a
// rename lands in one place.
function VerdictPill({ verdict, label, ...rest }: { verdict: Verdict; label?: string; 'data-testid'?: string }) {
  const tone = LIST_STATUS_DISPLAY[verdict].tone;
  return (
    <StatusPill $background={TONE_STYLES[tone].background} $color={TONE_STYLES[tone].color} {...rest}>
      {label ?? LIST_STATUS_DISPLAY[verdict].label}
    </StatusPill>
  );
}

export default function SNFSlideout({
  open,
  workflow,
  onClose,
}: {
  open: boolean;
  workflow: Workflow;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('snf');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  // Click a column to sort by it ascending; click the active column to flip direction.
  const toggleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  // SNFCell keeps this mounted across close/reopen (for the Foldout animation), so nothing else
  // resets this local state — without it, a search term or reordered sort from a previous look
  // silently carries into the next one.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSortColumn('snf');
    setSortDirection('asc');
  }, [open]);

  const { profile } = useProfile();

  // The list-page workflow carries a subset of values — facility_determination is only present
  // in the detail response. Fetch the full workflow when the slideout opens so status reads are
  // accurate.
  const { workflow: fullWorkflow } = useWorkflow(open ? workflow.id : undefined);

  // Holds the most recently FETCHED detail workflow, across close/reopen and through Foldout's
  // 500ms close-linger (Foldout.tsx renders {linger && children} for 500ms after `open` goes
  // false, but useWorkflow(undefined) drops fullWorkflow the instant `open` flips — without this,
  // every pill would flash back to "Pending Decision" on every close). Deliberately never seeded
  // from the `workflow` prop: the list payload has no facility_determination, so falling back to
  // it doesn't degrade to "unknown," it asserts a wrong status.
  const [lastLoadedWorkflow, setLastLoadedWorkflow] = useState<Workflow | undefined>(undefined);
  useEffect(() => {
    if (fullWorkflow) setLastLoadedWorkflow(fullWorkflow);
  }, [fullWorkflow]);

  const detailWorkflow = fullWorkflow ?? lastLoadedWorkflow;
  const detailLoaded = detailWorkflow !== undefined;
  // The prop's groups are correct but can lag (e.g. a facility added from another tab before the
  // list refetches) — once the detail fetch has landed at least once, it's the better source.
  const groups = detailWorkflow?.groups ?? workflow.groups;

  // A recorded outcome switches the table to the outcome variant (OLIO-3295). The list row carries
  // the outcome value too, so the header takes the right shape at once; the rows wait for the
  // detail payload because only it carries the per-building decisions. Never for a canceled or
  // archived referral — its pill says Archived / Canceled, and so does this table.
  const outcome =
    workflow.status === WorkflowStatus.CANCELED
      ? ''
      : recordedOutcome(detailWorkflow ?? workflow) || recordedOutcome(workflow);
  const badge = outcome ? outcomeBadge(detailWorkflow ?? workflow, outcome) : null;

  // Gated on `open` — SNFCell keeps this mounted while closed (for the Foldout animation), so an
  // ungated tick would re-render every multi-SNF row's hidden table every 30s. Off entirely for
  // the outcome variant, which shows no elapsed time.
  const now = useNow(30_000, open && !outcome);

  const clientName = profile.actingClient?.name ?? '';

  const rows = useMemo(
    () =>
      groups.map((group) => ({
        group,
        // null (not 'pending') while the first fetch is still in flight, so the Status column can
        // show a loading state rather than asserting a determination-free read as fact.
        status: detailWorkflow ? facilityDecisionStatus(detailWorkflow, group.id) : null,
        // Per-SNF "sent at" (OLIO-3240) — when this referral was sent to this building, so Time
        // Elapsed differs per SNF and can sort. Null on the rare group missing the field.
        sentAt: parseDate(group.sentAt) ?? null,
        // Freezes "Time to Decision" the moment this building has one (OLIO-3234 review): without
        // this, a row already reading Accepted/Denied kept ticking under a header that promises a
        // time TO a decision, not a time since one. Falls back to the referral's own concludedAt
        // for a building that never got one of its own — a canceled/archived referral (every
        // History row) has no per-facility decision to freeze on, so without this its clock kept
        // climbing past the moment the referral itself ended.
        decidedAt:
          (detailWorkflow ? parseDate(facilityDecision(detailWorkflow, group.id)?.completedAt) : null) ??
          workflow.concludedAt,
        ...(outcome && detailWorkflow ? outcomeRow(detailWorkflow, group.id, outcome, clientName) : {}),
      })),
    [groups, detailWorkflow, outcome, clientName, workflow.concludedAt]
  );

  const visible = useMemo(() => {
    // The span the Time to Decision column actually renders (duration(sentAt, decidedAt ?? now)) —
    // sorting on raw sentAt alone (OLIO-3234 review) put a facility that decided quickly above one
    // still pending longer, because sentAt no longer tracks what's on screen once a row freezes.
    // Missing sentAt sorts to the longest-elapsed end, same as before.
    const elapsedMs = (row: (typeof rows)[number]) =>
      row.sentAt ? (row.decidedAt ?? now).getTime() - row.sentAt.getTime() : Infinity;

    const query = search.trim().toLowerCase();
    const filtered = query ? rows.filter((r) => r.group.name.toLowerCase().includes(query)) : rows;
    return [...filtered].sort((a, b) => {
      // Ascending shows the shortest elapsed first. Ties (a referral sent to several SNFs at intake
      // shares one WorkflowGroup insert, so all sentAts match) fall back to the name compare —
      // otherwise the stable sort leaves them in API order and reads as broken rather than equal.
      const cmp =
        sortColumn === 'elapsed'
          ? elapsedMs(a) - elapsedMs(b) || a.group.name.localeCompare(b.group.name)
          : a.group.name.localeCompare(b.group.name);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [rows, search, sortColumn, sortDirection, now]);

  const subtitle = `Referral sent to ${sentToSummary(groups)}`;
  const title = outcome ? `${workflow.patientName} – Referral Outcome` : workflow.patientName;

  return (
    <DetailSlideout
      open={open}
      hideBackButton
      onClose={onClose}
      title={title}
      titleBadge={badge && <VerdictPill verdict={badge.verdict} label={badge.label} data-testid='slideout-outcome' />}
      subtitle={subtitle}>
      <Controls>
        <SearchWrap>
          <Search value={search} onChange={setSearch} placeholder='Search' />
        </SearchWrap>
      </Controls>
      <Table>
        <thead>
          <tr>
            <ColHeader $sortable onClick={() => toggleSort('snf')}>
              SNF {sortColumn === 'snf' && <SortArrow>{sortDirection === 'asc' ? '↑' : '↓'}</SortArrow>}
            </ColHeader>
            {outcome ? (
              <>
                <ColHeader>Referral Outcome</ColHeader>
                <ColHeader>Reason</ColHeader>
              </>
            ) : (
              <>
                <ColHeader>Status</ColHeader>
                <ColHeader $sortable onClick={() => toggleSort('elapsed')}>
                  Time to Decision{' '}
                  {sortColumn === 'elapsed' && <SortArrow>{sortDirection === 'asc' ? '↑' : '↓'}</SortArrow>}
                </ColHeader>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {outcome && !detailLoaded && (
            <tr>
              <EmptyCell colSpan={3} data-testid='snf-outcome-loading'>
                Loading…
              </EmptyCell>
            </tr>
          )}
          {visible.map(({ group, status, sentAt, decidedAt, verdict, reason }) => {
            if (outcome && !detailLoaded) return null;
            if (verdict) {
              return (
                <Tr key={group.id}>
                  <SnfTd>{group.name}</SnfTd>
                  <Td>
                    <VerdictPill verdict={verdict} data-testid='snf-outcome' />
                  </Td>
                  <Td data-testid='snf-reason'>{reason}</Td>
                </Tr>
              );
            }
            const display = status ? FACILITY_STATUS_DISPLAY[status] : null;
            return (
              <Tr key={group.id}>
                <SnfTd>{group.name}</SnfTd>
                <Td>
                  {display ? (
                    <StatusPill $background={display.background} $color={display.color}>
                      {display.label}
                    </StatusPill>
                  ) : (
                    <StatusPlaceholder data-testid='status-loading' />
                  )}
                </Td>
                <Td>{sentAt ? duration(sentAt, decidedAt ?? now) : '—'}</Td>
              </Tr>
            );
          })}
          {visible.length === 0 && (!outcome || detailLoaded) && (
            <tr>
              <EmptyCell colSpan={3}>No SNFs match your search.</EmptyCell>
            </tr>
          )}
        </tbody>
      </Table>
    </DetailSlideout>
  );
}

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchWrap = styled.div`
  width: 320px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  cursor: default;
`;

const ColHeader = styled.th<{ $sortable?: boolean }>`
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black50};
  border-bottom: 1px solid ${colors.black10};
  white-space: nowrap;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    color: ${({ $sortable }) => ($sortable ? colors.black100 : colors.black50)};
  }
`;

const SortArrow = styled.span`
  margin-left: 4px;
  font-size: 11px;
`;

const Tr = styled.tr`
  & + & td {
    border-top: 1px solid ${colors.black10};
  }
`;

const Td = styled.td`
  padding: 12px;
  vertical-align: middle;
`;

const SnfTd = styled(Td)`
  font-weight: 500;
`;

const StatusPill = styled.span<{ $background: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 24px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
`;

// Stands in for StatusPill while the first detail fetch is still in flight — same footprint,
// no label, so the row doesn't assert a determination-free read as the real status.
const StatusPlaceholder = styled.span`
  display: inline-block;
  width: 88px;
  height: 18px;
  border-radius: 24px;
  background-color: ${colors.black5};
`;

const EmptyCell = styled.td`
  padding: 20px 12px;
  color: ${colors.black50};
  font-size: 12px;
`;
